/**
 * Add the native Shopify selling plan to each Atlas stack component.
 *
 * Atlas's bundle-product request already contains the `_bundle*` properties
 * used by its discount function. This shim changes only `selling_plan` on the
 * two component lines when the selected offer is the subscription offer.
 */
(function lulloAtlasStackPlans() {
  'use strict';

  const configElement = document.getElementById('LulloAtlasStackPlanConfig');
  if (!configElement || typeof window.fetch !== 'function') return;

  const normalizeText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
  const positiveInteger = (value) => {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : null;
  };

  let sourceConfig;
  try {
    sourceConfig = JSON.parse(configElement.textContent || '{}');
  } catch (error) {
    console.error('[LULLO] Atlas stack plan configuration is invalid.', error);
    return;
  }

  const subscriptionOfferTitles = new Set(
    [
      sourceConfig.subscriptionOfferTitle || 'SUBSCRIBE & SAVE 25%',
      ...(Array.isArray(sourceConfig.subscriptionOfferTitles)
        ? sourceConfig.subscriptionOfferTitles
        : [])
    ]
      .map(normalizeText)
      .filter(Boolean)
  );
  const oneTimeOfferTitles = new Set(
    [
      sourceConfig.oneTimeOfferTitle || 'ONE-TIME PURCHASE',
      ...(Array.isArray(sourceConfig.oneTimeOfferTitles) ? sourceConfig.oneTimeOfferTitles : [])
    ]
      .map(normalizeText)
      .filter(Boolean)
  );
  const bundles = new Map();

  Object.entries(sourceConfig.bundles || {}).forEach(([bundleIdValue, bundleConfig]) => {
    const bundleId = positiveInteger(bundleIdValue);
    if (!bundleId) return;

    const variants = new Map();
    (bundleConfig.components || []).forEach((component) => {
      const variantId = positiveInteger(component.variantId);
      const sellingPlanId = positiveInteger(component.sellingPlanId);
      if (variantId && sellingPlanId) variants.set(String(variantId), sellingPlanId);
    });

    bundles.set(String(bundleId), {
      expectedComponentCount: positiveInteger(bundleConfig.expectedComponentCount) || 2,
      variants
    });
  });

  if (bundles.size === 0) return;

  const fetchMarker = Symbol.for('lullo.atlasStackPlanFetch.v1');
  const ignoredLine = (properties) =>
    properties?._gift === true ||
    properties?._gift === 'true' ||
    properties?._upsell === true ||
    properties?._upsell === 'true' ||
    properties?._progressive_gift === true ||
    properties?._progressive_gift === 'true';

  function isCartAddRequest(input) {
    const rawUrl = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
    if (!rawUrl) return false;

    try {
      const pathname = new URL(rawUrl, window.location.origin).pathname;
      return /\/cart\/add(?:\.js)?$/.test(pathname);
    } catch (_error) {
      return false;
    }
  }

  function selectedOfferTitle(bundleId) {
    const root = document.querySelector(`.atlas-bundler-render.bundle-qty-deal-${bundleId}`);
    if (!root) return '';

    const selectedLabel = Array.from(root.querySelectorAll('label.bundle-qty-option')).find((label) => {
      const inputId = label.htmlFor || label.getAttribute('for');
      return inputId ? Boolean(document.getElementById(inputId)?.checked) : false;
    });

    return normalizeText(selectedLabel?.querySelector('.bundle-qty-title')?.textContent);
  }

  function requestMode(bundleId, lines) {
    const titles = [
      ...new Set(
        lines
          .map(({ item }) => normalizeText(item.properties?._offer_title))
          .filter(Boolean)
      )
    ];

    const hasSubscriptionTitle = titles.some((title) => subscriptionOfferTitles.has(title));

    const hasOneTimeTitle = titles.some((title) => oneTimeOfferTitles.has(title));

    if (hasSubscriptionTitle && hasOneTimeTitle) {
      throw new Error(`Atlas bundle ${bundleId} mixed one-time and subscription component lines.`);
    }
    if (hasSubscriptionTitle) return 'subscription';
    if (hasOneTimeTitle) return 'one-time';
    return subscriptionOfferTitles.has(selectedOfferTitle(bundleId)) ? 'subscription' : 'unknown';
  }

  function rewritePayload(payload) {
    if (!payload || !Array.isArray(payload.items)) return { changed: false, payload };

    const groups = new Map();
    payload.items.forEach((item, index) => {
      const properties = item?.properties;
      const bundleId = String(properties?._bundle ?? '');
      if (!bundles.has(bundleId) || ignoredLine(properties)) return;
      if (!groups.has(bundleId)) groups.set(bundleId, []);
      groups.get(bundleId).push({ index, item });
    });

    if (groups.size === 0) return { changed: false, payload };

    const items = payload.items.slice();
    let changed = false;

    groups.forEach((lines, bundleId) => {
      if (requestMode(bundleId, lines) !== 'subscription') return;

      const bundle = bundles.get(bundleId);
      if (lines.length !== bundle.expectedComponentCount) {
        throw new Error(
          `Atlas bundle ${bundleId} expected ${bundle.expectedComponentCount} subscription components but received ${lines.length}.`
        );
      }

      lines.forEach(({ index, item }) => {
        const variantId = String(positiveInteger(item.id) || '');
        const sellingPlanId = bundle.variants.get(variantId);
        if (!sellingPlanId) {
          throw new Error(`Atlas bundle ${bundleId} has no verified selling plan for variant ${variantId || 'unknown'}.`);
        }
        items[index] = { ...item, selling_plan: sellingPlanId };
        changed = true;
      });
    });

    return { changed, payload: changed ? { ...payload, items } : payload };
  }

  function displayAddError(error) {
    const match = String(error?.message || '').match(/Atlas bundle (\d+)/);
    const bundleId = match?.[1];
    const root = bundleId
      ? document.querySelector(`.atlas-bundler-render.bundle-qty-deal-${bundleId}`)
      : null;
    const container = root?.closest('.bundle-container');
    const message = container?.querySelector('.bundle-qty-error-message');

    if (message) {
      message.textContent =
        "We couldn't add the subscription yet. Please refresh and try again, or choose one-time purchase.";
      message.setAttribute('role', 'alert');
      message.style.display = 'block';
    }

    console.error('[LULLO] Atlas stack subscription add was stopped safely.', error);
    if (typeof window.CustomEvent === 'function') {
      document.dispatchEvent(
        new window.CustomEvent('lullo:atlas-stack-plan-error', {
          detail: { bundleId: bundleId || null }
        })
      );
    }
  }

  function wrapFetch(nextFetch) {
    const wrappedFetch = function lulloAtlasStackPlanFetch(input, init) {
      if (!isCartAddRequest(input) || typeof init?.body !== 'string') {
        return nextFetch.call(this, input, init);
      }

      let payload;
      try {
        payload = JSON.parse(init.body);
      } catch (_error) {
        return nextFetch.call(this, input, init);
      }

      try {
        const rewritten = rewritePayload(payload);
        if (!rewritten.changed) return nextFetch.call(this, input, init);
        return nextFetch.call(this, input, {
          ...init,
          body: JSON.stringify(rewritten.payload)
        });
      } catch (error) {
        displayAddError(error);
        return Promise.reject(error);
      }
    };

    Object.defineProperty(wrappedFetch, fetchMarker, { value: true });
    return wrappedFetch;
  }

  function ensureInstalled() {
    if (typeof window.fetch !== 'function' || window.fetch[fetchMarker]) return;
    window.fetch = wrapFetch(window.fetch);
  }

  ensureInstalled();
  window.addEventListener('pageshow', ensureInstalled);
  document.addEventListener('shopify:section:load', ensureInstalled);
  document.addEventListener(
    'click',
    (event) => {
      if (event.target?.closest?.('button[name="atlas-bundler-add"]')) ensureInstalled();
    },
    true
  );

  let checks = 0;
  const installationCheck = window.setInterval(() => {
    ensureInstalled();
    checks += 1;
    if (checks >= 40) window.clearInterval(installationCheck);
  }, 250);
})();
